import * as AppConstants from 'app/AppConstants';
import {
  BUZZ_FAVORITE_POSITIONS,
  BUZZ_MESSAGE_TYPES,
} from 'constants/BuzzConstants';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import reduce from 'lodash/reduce';

export const defaultState = {
  channels: {},
  channelMetadata: {},
  pinnedChannelIds: new Set(),
  loadedMessagesChannelIds: new Set(),
  lastViewedMessageByChannelId: {},
  isLoadingMessagesByChannelId: {},
  isFinishedLoadingMessagesByChannelId: {},
  channelIdsByCardId: {},
  channelIdsByPageId: {},
  channelIdsByUserId: {},
  channelIdsInGeneralTopicBrowserPagination: new Set(),
  channelIdsInCardTopicBrowserPagination: new Set(),
  channelIdsInPageTopicBrowserPagination: new Set(),
  channelIdsInGeneralTopicBrowserSearch: new Set(),
  channelIdsInCardTopicBrowserSearch: new Set(),
  channelIdsInPageTopicBrowserSearch: new Set(),
  nextOffsetOfGeneralTopicBrowserPagination: 0,
  nextOffsetOfCardTopicBrowserPagination: 0,
  nextOffsetOfPageTopicBrowserPagination: 0,
  isLoadingGeneralTopicsByOffset: false,
  isLoadingCardTopicsByOffset: false,
  isLoadingPageTopicsByOffset: false,
  isComposingHuddle: false,
  isCreatingHuddle: false,
  composeReplyMessageId: null,
  grantPreviews: [],
  grantPreviewsProperties: {},
};

const mergeChannelMetadata = (prevMeta, meta) =>
  reduce(
    meta,
    (memo, value, key) => {
      const prev = prevMeta[key];
      const isOlder =
        prev && prev.lastViewedTimestamp > value.lastViewedTimestamp;
      memo[key] = isOlder ? prev : {...prev, ...value};
      return memo;
    },
    {...prevMeta}
  );

const parseChannelMetadata = me =>
  reduce(
    me,
    (memo, value, key) => {
      const [type, id] = key.split(':');
      if (type === 'CHANNEL') {
        memo[id] = value;
      }

      return memo;
    },
    {}
  );

const incrementCount = (meta = {}, group, type) => ({
  ...meta,
  [group]: {
    huddleMessages: 0,
    messages: 0,
    ...meta[group],
    [type]: get(meta, [group, type], 0) + 1,
  },
});

export default (state = defaultState, action) => {
  switch (action.type) {
    case AppConstants.BUZZ__MY_CHANNEL_UPDATED:
    case AppConstants.BUZZ__CHANNEL_LOADED_WITH_PEOPLE:
    case AppConstants.BUZZ__CHANNEL_LOADED: {
      const channelId = action.payload.channel.id;
      const channelMetadata = mergeChannelMetadata(
        state.channelMetadata,
        parseChannelMetadata(action.payload.me)
      );
      const meta = channelMetadata[channelId];
      const isPinned = meta.pin;
      const pinnedChannelIds = new Set(state.pinnedChannelIds);

      if (isPinned) {
        pinnedChannelIds.add(channelId);
      } else {
        pinnedChannelIds.delete(channelId);
      }

      return {
        ...state,
        pinnedChannelIds,
        channelMetadata,
        channels: {
          ...state.channels,
          [channelId]: {
            ...state.channels[channelId],
            ...action.payload.channel,
          },
        },
      };
    }

    case AppConstants.BUZZ__LOADED_ALL_CHANNELS:
    case AppConstants.BUZZ__LOADED_RECENT_ACTIVITY_TOPIC_CHANNELS: {
      const channels = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {...state.channels, ...channels},
        channelMetadata: mergeChannelMetadata(
          state.channelMetadata,
          parseChannelMetadata(action.payload.me)
        ),
      };
    }

    case AppConstants.BUZZ__LOAD_GENERAL_TOPICS_BY_OFFSET_STARTED: {
      return {
        ...state,
        isLoadingGeneralTopicsByOffset: true,
      };
    }

    case AppConstants.BUZZ__LOAD_CARD_TOPICS_BY_OFFSET_STARTED: {
      return {
        ...state,
        isLoadingCardTopicsByOffset: true,
      };
    }

    case AppConstants.BUZZ__LOAD_PAGE_TOPICS_BY_OFFSET_STARTED: {
      return {
        ...state,
        isLoadingPageTopicsByOffset: true,
      };
    }

    case AppConstants.BUZZ__LOAD_GENERAL_TOPICS_BY_OFFSET_FINISHED: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInGeneralTopicBrowserPagination: new Set([
          ...state.channelIdsInGeneralTopicBrowserPagination,
          ...Object.keys(newChannelsKeyedById),
        ]),
        nextOffsetOfGeneralTopicBrowserPagination: action.payload.nextOffset,
        isLoadingGeneralTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOAD_CARD_TOPICS_BY_OFFSET_FINISHED: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInCardTopicBrowserPagination: new Set([
          ...state.channelIdsInCardTopicBrowserPagination,
          ...Object.keys(newChannelsKeyedById),
        ]),
        nextOffsetOfCardTopicBrowserPagination: action.payload.nextOffset,
        isLoadingCardTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOAD_PAGE_TOPICS_BY_OFFSET_FINISHED: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInPageTopicBrowserPagination: new Set([
          ...state.channelIdsInPageTopicBrowserPagination,
          ...Object.keys(newChannelsKeyedById),
        ]),
        nextOffsetOfPageTopicBrowserPagination: action.payload.nextOffset,
        isLoadingPageTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOAD_GENERAL_TOPICS_BY_OFFSET_FAILED: {
      return {
        ...state,
        isLoadingGeneralTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOAD_CARD_TOPICS_BY_OFFSET_FAILED: {
      return {
        ...state,
        isLoadingCardTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOAD_PAGE_TOPICS_BY_OFFSET_FAILED: {
      return {
        ...state,
        isLoadingPageTopicsByOffset: false,
      };
    }

    case AppConstants.BUZZ__LOADED_GENERAL_TOPICS_BY_TITLE_SEARCH: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInGeneralTopicBrowserSearch: new Set(
          Object.keys(newChannelsKeyedById)
        ),
      };
    }

    case AppConstants.BUZZ__LOADED_CARD_TOPICS_BY_TITLE_SEARCH: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInCardTopicBrowserSearch: new Set(
          Object.keys(newChannelsKeyedById)
        ),
      };
    }

    case AppConstants.BUZZ__LOADED_PAGE_TOPICS_BY_TITLE_SEARCH: {
      const newChannelsKeyedById = keyBy(action.payload.channels, 'id');
      return {
        ...state,
        channels: {
          ...state.channels,
          ...newChannelsKeyedById,
        },
        channelIdsInPageTopicBrowserSearch: new Set(
          Object.keys(newChannelsKeyedById)
        ),
      };
    }

    case AppConstants.BUZZ__LOADED_PINNED_CHANNELS: {
      const pinnedChannelIds = keyBy(action.payload.channels, 'id');
      const channelsWithHuddles = action.payload.channels.filter(
        channel => channel.huddles
      );
      const huddles = flatMap(channelsWithHuddles, channel =>
        channel.huddles.map(huddle => ({...huddle, channelId: channel.id}))
      );
      const keyedHuddles = keyBy(huddles, 'id');
      return {
        ...state,
        channels: {...state.channels, ...pinnedChannelIds, ...keyedHuddles},
        channelMetadata: mergeChannelMetadata(
          state.channelMetadata,
          parseChannelMetadata(action.payload.me)
        ),
        pinnedChannelIds: new Set(Object.keys(pinnedChannelIds)),
      };
    }

    case AppConstants.BUZZ__FAVORITED_CHANNEL_BY_ID: {
      const {channelId, favoritePosition} = action.payload;
      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...state.channelMetadata[channelId],
            favoritePosition,
          },
        },
      };
    }

    case AppConstants.BUZZ__UNFAVORITED_CHANNEL_BY_ID: {
      const channelId = action.payload.channelId;
      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...state.channelMetadata[channelId],
            favoritePosition: BUZZ_FAVORITE_POSITIONS.UNFAVORITED,
          },
        },
      };
    }

    case AppConstants.BUZZ__PINNED_CHANNEL_BY_ID:
    case AppConstants.BUZZ__UNPINNED_CHANNEL_BY_ID_FAILED: {
      const channelId = action.payload.channelId;
      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...state.channelMetadata[channelId],
            ...action.payload.meta,
          },
        },
        pinnedChannelIds: new Set(state.pinnedChannelIds).add(channelId),
      };
    }

    case AppConstants.BUZZ__UNPINNED_CHANNEL_BY_ID_STARTED: {
      const pinned = new Set(state.pinnedChannelIds);
      const channelId = action.payload.channelId;
      pinned.delete(channelId);
      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...state.channelMetadata[channelId],
            ...action.payload.meta,
          },
        },
        pinnedChannelIds: pinned,
      };
    }

    case AppConstants.BUZZ__USER_CREATE_MESSAGE_FINISHED:
    case AppConstants.BUZZ__MESSAGE_CREATED_BY_SERVER: {
      const {channelId, created} = action.payload.message;
      const meta = state.channelMetadata[channelId] || {};
      const count = incrementCount(meta, 'unreadCounts', 'messages');

      return {
        ...state,
        channels: {
          ...state.channels,
          [channelId]: {
            ...state.channels[channelId],
            lastUpdated: created,
          },
        },
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...meta,
            ...count,
          },
        },
      };
    }

    case AppConstants.BUZZ__HUDDLE_MESSAGE_CREATED_BY_SERVER: {
      const {channelId, rootChannelId, created} = action.payload.message;
      const meta = state.channelMetadata[channelId] || {};
      const rootChannelMeta = state.channelMetadata[rootChannelId] || {};
      const count = incrementCount(meta, 'unreadCounts', 'messages');
      const parentCount = incrementCount(
        rootChannelMeta,
        'unreadCounts',
        'huddleMessages'
      );

      return {
        ...state,
        channels: {
          ...state.channels,
          [channelId]: {
            ...state.channels[channelId],
            lastUpdated: created,
          },
        },
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...meta,
            ...count,
          },
          [rootChannelId]: {
            ...rootChannelMeta,
            ...parentCount,
          },
        },
      };
    }

    case AppConstants.BUZZ__USER_AT_MENTIONED: {
      const channelId = action.payload.channelId;
      const meta = state.channelMetadata[channelId] || {};
      const count = incrementCount(meta, 'importantCounts', 'messages');

      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...meta,
            ...count,
          },
        },
      };
    }

    case AppConstants.BUZZ__USER_AT_MENTIONED_IN_HUDDLE: {
      const {channelId, parentChannelId} = action.payload;
      const meta = state.channelMetadata[channelId] || {};
      const parentChannelMeta = state.channelMetadata[parentChannelId] || {};
      const count = incrementCount(meta, 'importantCounts', 'messages');
      const parentCount = incrementCount(
        parentChannelMeta,
        'importantCounts',
        'huddleMessages'
      );

      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...meta,
            ...count,
          },
          [parentChannelId]: {
            ...parentChannelMeta,
            ...parentCount,
          },
        },
      };
    }

    case AppConstants.BUZZ__CHANNEL_CREATED:
      return {
        ...state,
        channels: {
          ...state.channels,
          [action.payload.id]: action.payload,
        },
        pinnedChannelIds: new Set(state.pinnedChannelIds).add(
          action.payload.id
        ),
      };

    case AppConstants.BUZZ__CHANNEL_UNREADS_CLEARED: {
      const channelId = action.payload.id;

      return {
        ...state,
        channelMetadata: {
          ...state.channelMetadata,
          [channelId]: {
            ...state.channelMetadata[channelId],
            importantCounts: {
              huddleMessages: 0,
              messages: 0,
            },
            unreadCounts: {
              huddleMessages: 0,
              messages: 0,
            },
          },
        },
        lastViewedMessageByChannelId: {
          ...state.lastViewedMessageByChannelId,
          [channelId]: action.payload.messageId,
        },
      };
    }

    case AppConstants.BUZZ__ALL_UNREADS_CLEARED: {
      return {
        ...state,
        channelMetadata: mapValues(state.channelMetadata, channel => ({
          ...channel,
          unreadCounts: {
            messages: 0,
            huddleMessages: 0,
          },
          importantCounts: {
            messages: 0,
            huddleMessages: 0,
          },
          importantHuddles: false,
        })),
      };
    }

    case AppConstants.BUZZ__FLAT_MESSAGES_LOADED_ALL_MESSAGES:
      return {
        ...state,
        isFinishedLoadingMessagesByChannelId: {
          ...state.isLoadingMessagesByChannelId,
          [action.payload.channelId]: true,
        },
      };

    case AppConstants.BUZZ__LOAD_MESSAGES_STARTED:
      return {
        ...state,
        isLoadingMessagesByChannelId: {
          ...state.isLoadingMessagesByChannelId,
          [action.payload.channelId]: true,
        },
      };

    case AppConstants.BUZZ__FLAT_MESSAGES_LOADED: {
      const {messages} = action.payload;
      const huddles = messages.filter(
        m => m.type === BUZZ_MESSAGE_TYPES.HUDDLE
      );
      const keyedHuddles = keyBy(huddles, 'id');

      return {
        ...state,
        channels: {
          ...state.channels,
          ...keyedHuddles,
        },
        isLoadingMessagesByChannelId: {
          ...state.isLoadingMessagesByChannelId,
          [action.payload.channelId]: false,
        },
        channelMetadata: mergeChannelMetadata(
          state.channelMetadata,
          parseChannelMetadata(action.payload.me)
        ),
      };
    }

    case AppConstants.BUZZ__CHANNELS_LOADED_BY_CARD_IDS: {
      const {channels} = action.payload;
      const keyedChannels = keyBy(channels, 'id');
      const keyedCards = channels.reduce((memo, channel) => {
        memo[channel.objectId] = channel.id;
        return memo;
      }, {});
      return {
        ...state,
        channelIdsByCardId: {
          ...state.channelIdsByCardId,
          ...keyedCards,
        },
        channels: {
          ...state.channels,
          ...keyedChannels,
        },
      };
    }

    case AppConstants.BUZZ__CHANNEL_LOADED_BY_PAGE_ID:
      return {
        ...state,
        channelIdsByPageId: {
          ...state.channelIdsByPageId,
          [action.payload.pageId]: action.payload.channel.id,
        },
        channels: {
          ...state.channels,
          [action.payload.channel.id]: action.payload.channel,
        },
      };

    case AppConstants.BUZZ__CHANNEL_LOADED_BY_USER_ID: {
      return {
        ...state,
        channelIdsByUserId: {
          ...state.channelIdsByUserId,
          [action.payload.userId]: action.payload.channel.id,
        },
        channels: {
          ...state.channels,
          [action.payload.channel.id]: action.payload.channel,
        },
      };
    }

    case AppConstants.BUZZ__CREATE_HUDDLE_STARTED: {
      return {
        ...state,
        isCreatingHuddle: true,
      };
    }

    case AppConstants.BUZZ__CREATE_HUDDLE_FINISHED: {
      return {
        ...state,
        isComposingHuddle: false,
        isCreatingHuddle: false,
        channels: {
          ...state.channels,
          [action.payload.channel.id]: action.payload.channel,
        },
      };
    }

    case AppConstants.BUZZ__CREATE_HUDDLE_FAILED: {
      return {
        ...state,
        isCreatingHuddle: false,
      };
    }

    case AppConstants.BUZZ__COMPOSE_HUDDLE_STARTED: {
      return {
        ...state,
        isComposingHuddle: true,
        composeReplyMessageId: action.payload.replyMessageId || null,
      };
    }

    case AppConstants.BUZZ__COMPOSE_HUDDLE_CANCELLED: {
      return {
        ...state,
        isComposingHuddle: false,
        composeReplyMessageId: null,
      };
    }

    case AppConstants.BUZZ__CLEARED_GRANT_PREVIEWS:
      return {
        ...state,
        grantPreviews: [],
        grantPreviewsProperties: {},
      };

    case AppConstants.BUZZ__LOADED_GRANT_PREVIEWS:
      return {
        ...state,
        grantPreviews: action.payload.grantPreviews,
        grantPreviewsProperties: action.payload.grantPreviewsProperties,
      };

    default:
      return state;
  }
};
